'use client';

import { useEffect, useState } from 'react';
import TemplatePreviewFrame from '@/app/(web)/(public)/template/[slug]/TemplatePreviewFrame';
import { cleanTemplateName } from '@/lib/templates-data';
import { appUrl, apiUrl } from '@/lib/config';
import { Loader2 } from 'lucide-react';

export default function NotFoundClientFallback() {
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState<{
    id?: string;
    slug?: string;
    name?: string;
    category?: string;
    html?: string;
  } | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const pathname = window.location.pathname;

    // Only activate for /template/<slug> paths
    if (pathname.startsWith('/template/')) {
      const slug = pathname.replace(/^\/template\/?/, '').replace(/\/$/, '');
      if (slug && slug !== 'template') {
        setLoading(true);
        fetch(apiUrl(`/api/templates/${encodeURIComponent(slug)}`))
          .then(async (res) => {
            if (res.ok) {
              const data = await res.json();
              if (data && (data.id || data.slug)) {
                let html = data.html;
                if (!html && data.id) {
                  try {
                    const renderRes = await fetch(apiUrl(`/api/render-email/${data.id}?preview=true`));
                    if (renderRes.ok) {
                      html = await renderRes.text();
                    }
                  } catch {
                    // ignore
                  }
                }
                data.html = html || '';
                setTemplate(data);
              }
            }
          })
          .catch(() => {
            // ignore network errors, let default 404 show
          })
          .finally(() => setLoading(false));
      }
    }
  }, []);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background text-on-surface">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
        <p className="text-sm font-medium text-on-surface-variant">Loading template preview...</p>
      </div>
    );
  }

  if (template && template.html) {
    const displayName = cleanTemplateName(template.name || 'Email Template');
    const editorUrl = appUrl(
      `/editor/new?templateId=${encodeURIComponent(template.id || template.slug || '')}`
    );
    return (
      <div className="fixed inset-0 z-50 overflow-auto bg-background">
        <TemplatePreviewFrame
          html={template.html}
          templateName={displayName}
          category={template.category || 'General'}
          editorUrl={editorUrl}
          templateSlug={template.slug || template.id}
          templateId={template.id}
        />
      </div>
    );
  }

  return null;
}
